import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const REASONS = [
  { key: "safety", label: "Safety concern or harassment" },
  { key: "misleading", label: "Misleading credentials or profile" },
  { key: "no_show", label: "No-show or unresponsive" },
  { key: "inappropriate", label: "Inappropriate behavior" },
  { key: "spam", label: "Spam or off-platform solicitation" },
  { key: "other", label: "Something else" },
];

export default function ReportCoachDialog({ coachSlug, coachName, open, onClose }) {
  const [reason, setReason] = useState("safety");
  const [details, setDetails] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/coaches/${coachSlug}/report`, {
        reason,
        details: details.trim() || null,
        reporter_email: email.trim() || null,
      });
      toast.success("Report submitted. Our team will review it.");
      setDetails(""); setEmail(""); setReason("safety");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit report.");
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-center px-4"
          onClick={onClose}
        >
          <motion.form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
            initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 16, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            data-testid="report-coach-dialog"
            className="w-full max-w-lg bg-white rounded-2xl p-6 md:p-7"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-amber-50 border border-amber-200 grid place-items-center">
                  <AlertTriangle className="w-4 h-4 text-amber-700" strokeWidth={2.4} />
                </div>
                <div>
                  <div className="eyebrow">§ Report coach</div>
                  <h3 className="mt-1 font-display text-xl tracking-tight text-slate-900">
                    Report {coachName}?
                  </h3>
                </div>
              </div>
              <button type="button" onClick={onClose} className="p-2 -m-2 text-slate-400 hover:text-slate-900">
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="mt-4 text-[13px] text-slate-500 leading-relaxed">
              Reports are reviewed by the Coachly trust team. Serious safety issues are prioritized within 24h.
            </p>

            <div className="mt-5">
              <label className="text-[11px] tracking-[0.24em] uppercase text-slate-500 font-semibold">Reason</label>
              <div className="mt-2 grid gap-1.5">
                {REASONS.map((r) => (
                  <label key={r.key} className={`flex items-center gap-3 px-3 h-11 rounded-xl border cursor-pointer transition-colors ${
                    reason === r.key ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-300"
                  }`}>
                    <input
                      type="radio"
                      name="reason"
                      value={r.key}
                      checked={reason === r.key}
                      onChange={() => setReason(r.key)}
                      data-testid={`report-reason-${r.key}`}
                      className="sr-only"
                    />
                    <span className="text-[13.5px] font-medium">{r.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <label className="text-[11px] tracking-[0.24em] uppercase text-slate-500 font-semibold">
                Details <span className="normal-case tracking-normal text-slate-400">(optional)</span>
              </label>
              <textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                data-testid="report-details"
                rows={3}
                maxLength={1200}
                placeholder="What happened? Times, screenshots — anything helpful."
                className="mt-2 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25 resize-y"
              />
            </div>

            <div className="mt-4">
              <label className="text-[11px] tracking-[0.24em] uppercase text-slate-500 font-semibold">
                Your email <span className="normal-case tracking-normal text-slate-400">(optional — for follow-up)</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="report-reporter-email"
                placeholder="you@domain.com"
                className="mt-2 w-full h-11 rounded-xl bg-slate-50 border border-slate-200 px-4 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25"
              />
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button type="button" onClick={onClose} className="h-11 px-5 rounded-full text-[13px] font-semibold text-slate-600 hover:text-slate-900">
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                data-testid="report-submit"
                className="h-11 px-5 rounded-full bg-slate-900 hover:bg-red-600 text-white text-[13.5px] font-semibold inline-flex items-center gap-2 transition-colors disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
                Submit report
              </button>
            </div>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
